import type { ElementType, ReactNode, ComponentPropsWithoutRef } from 'react'

type SchemaWrapperProps<T extends ElementType> = {
  as?: T

  /**
   * Optional schema.org type.
   * If provided, we add itemScope + itemType.
   * e.g. "ItemList", "ListItem", or full URL.
   */
  type?: string

  /**
   * Optional itemProp (e.g. "itemListElement", "mainEntity", etc.)
   * If omitted, we don't add itemProp.
   */
  prop?: string

  children?: ReactNode
} & Omit<
  ComponentPropsWithoutRef<T>,
  'as' | 'children' | 'itemScope' | 'itemType' | 'itemProp'
>

function SchemaWrapper<T extends ElementType = 'div'>(
  props: SchemaWrapperProps<T>
) {
  const { as, type, prop, children, ...rest } = props
  const Component = as || 'div'

  const schemaProps: {
    itemScope?: true
    itemType?: string
    itemProp?: string
  } = {}

  if (type) {
    const itemType =
      type.startsWith('http://') || type.startsWith('https://')
        ? type
        : `https://schema.org/${type}`

    schemaProps.itemScope = true
    schemaProps.itemType = itemType
  }

  if (prop) {
    schemaProps.itemProp = prop
  }

  return (
    <Component
      {...schemaProps}
      {...rest}
    >
      {children}
    </Component>
  )
}

export default SchemaWrapper
